import React from "react";

import { AvatarProfile, Link, Skeleton, Table, Text } from "@performyard/py-components";
import type { PYColumnDef } from "@performyard/py-components";

import { employeeDetailsLinkWithFrom } from "../../../utils/employeeDetailsBreadcrumbs";

import type { DirectoryColumn, DirectoryRow } from "./rows";

// The py-components Table seeds `loading=true` with empty-object rows and relies on its default
// cellRendererSelector to swap in a Skeleton — that selector is bypassed when a column supplies
// its own cellRenderer, so each custom renderer has to opt back in.
const isLoadingRow = (row: Partial<DirectoryRow>) => !row?._id;

type DirectoryTableProps = {
	rows: DirectoryRow[];
	columns: DirectoryColumn[];
	loading: boolean;
	currentUserId?: string;
};

const DirectoryTable = ({ rows, columns, loading, currentUserId }: DirectoryTableProps): React.ReactElement => {
	const columnDefs: PYColumnDef<DirectoryRow>[] = [
		{
			// Display Name is always the first column and is not configurable.
			colId: "_displayName",
			headerName: "Name",
			type: "employee",
			flex: 1,
			minWidth: 200,
			valueGetter: (params) => params.data?.employee,
			// The standard employee renderer always links to employee details; self links go to "/".
			cellRenderer: (params: { data: DirectoryRow }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;

				return (
					<div className="flex h-full items-center justify-start">
						<AvatarProfile
							forceHardReload
							employeeId={row.employee._id}
							employeeName={row.employee.name}
							href={employeeDetailsLinkWithFrom(row.employee._id, currentUserId)}
							data-testid={`link-${row.employee.name.replace(" ", "-")}`}
						/>
					</div>
				);
			},
		},
		...columns.map<PYColumnDef<DirectoryRow>>((column) => ({
			colId: column._id,
			headerName: column.name,
			flex: 1,
			minWidth: 150,
			cellRenderer: (params: { data: DirectoryRow }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;

				const value = row.values[column._id];
				if (!value) return null;

				if (value.kind === "people") {
					return (
						<div className="flex h-full min-w-0 items-center gap-1">
							{value.people.map((person, index) => (
								<React.Fragment key={person._id}>
									<Link
										forceHardReload
										href={employeeDetailsLinkWithFrom(person._id, currentUserId)}
										data-testid={`link-${person.name.replace(" ", "-")}`}
									>
										{person.name}
									</Link>
									{index < value.people.length - 1 ? "," : null}
								</React.Fragment>
							))}
						</div>
					);
				}

				return (
					<div className="flex h-full items-center">
						<Text size="paragraph-3" weight="regular" dangerouslySetClassName="min-w-0">
							<span className="block py-text-truncate">{value.text}</span>
						</Text>
					</div>
				);
			},
		})),
	];

	return (
		<div data-testid="user_table">
			<Table<DirectoryRow>
				autoHeight
				columnSizing="fit-grid"
				columnDefs={columnDefs}
				rowData={rows}
				loading={loading}
				getRowId={(params) => params.data._id}
				emptyStateText="No employees found"
			/>
		</div>
	);
};

export default DirectoryTable;
